import { computed, nextTick, onScopeDispose, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isNil } from 'lodash';

import { useEventHub } from '@satellite/composables/useEventHub';
import { matchSocketEventToEntity, SocketEventMatch } from '@satellite/helpers/socketEventMatch';
import assetVisitService from '@satellite/services/asset-visit-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';
import {
  EntityPresenterMode,
  isEntityPresenterQueryEnabled,
  isLazyLiveMode,
  isStaticMode,
  subscribesToEventHub
} from './presenterEntityMode';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { DebouncedSocketEvent } from '@satellite/helpers/socketEventTypes';
import type { PresenterPlaceholderData } from './presenterPlaceholder';
import type { YardAssetVisitDetails } from './yardAssetVisitPresenterShared';

// Hooking an asset writes only the asset_container row, so it emits no `update-AssetVisit`.
const ASSET_VISIT_EVENT_HUB_TOPICS = [
  'update-AssetVisit',
  'create-AssetContainer',
  'update-AssetContainer',
  'update-Appointment',
  'update-Dock'
] as const;

type AssetVisitEventHubHandlerMap = {
  'update-AssetVisit': (event: { id?: string; assetVisitId?: string }) => void;
  'create-AssetContainer': (event: { assetVisitId?: string }) => void;
  'update-AssetContainer': (event: { assetVisitId?: string }) => void;
  'update-Appointment': (event: { id?: string }) => void;
  'update-Dock': (event: { id?: string }) => void;
};

export interface UseYardAssetVisitFetcherOptions {
  /**
   * Asset visit ID to fetch. Can be a ref for reactivity.
   * Optional if initialAssetVisitData (with id) is provided.
   */
  assetVisitId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional initial asset visit data used as initialData for the query.
   */
  initialAssetVisitData?:
    | Ref<Maybe<YardAssetVisitDetails>>
    | ComputedRef<Maybe<YardAssetVisitDetails>>
    | YardAssetVisitDetails;

  /**
   * Optional incomplete asset visit data shown as placeholderData while the query fetches.
   */
  placeholderAssetVisitData?:
    | Ref<Maybe<PresenterPlaceholderData<YardAssetVisitDetails>>>
    | ComputedRef<Maybe<PresenterPlaceholderData<YardAssetVisitDetails>>>
    | PresenterPlaceholderData<YardAssetVisitDetails>;

  /**
   * When true, the query runs without viewport gating.
   * Default: false
   */
  headless?: boolean;

  /**
   * - `live`: fetches when enabled and subscribes to EventHub updates (default)
   * - `lazy-live`: no fetch until first matching EventHub event, refresh(), or invalidate()
   * - `static`: no fetch/subscriptions; returns staticAssetVisitData only
   */
  mode?: EntityPresenterMode;

  /**
   * Data source for `mode: 'static'`.
   */
  staticAssetVisitData?:
    | Ref<Maybe<YardAssetVisitDetails>>
    | ComputedRef<Maybe<YardAssetVisitDetails>>
    | YardAssetVisitDetails;

  staleTime?: number;

  onUpdateReceived?: (assetVisit: YardAssetVisitDetails) => void;
}

export function useYardAssetVisitFetcher(options: UseYardAssetVisitFetcherOptions) {
  const {
    assetVisitId,
    initialAssetVisitData,
    placeholderAssetVisitData,
    headless = false,
    mode = EntityPresenterMode.Live,
    staticAssetVisitData,
    staleTime = Infinity,
    onUpdateReceived
  } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveAssetVisitId = computed((): string | null => {
    const explicitId = unref(assetVisitId);
    const fromObject = toValue(initialAssetVisitData)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardAssetVisitFetcher] assetVisitId mismatch:',
          `explicit assetVisitId (${explicitId}) does not match assetVisit.id (${fromObject}). Using explicit assetVisitId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const initialAssetVisit = computed(() => toValue(initialAssetVisitData) ?? undefined);
  const placeholderAssetVisit = computed(() => toValue(placeholderAssetVisitData) ?? undefined);
  const staticAssetVisit = computed(() => toValue(staticAssetVisitData) ?? undefined);

  if (
    isStaticMode(mode) &&
    (!isNil(toValue(initialAssetVisitData)) || !isNil(toValue(placeholderAssetVisitData)))
  ) {
    console.warn(
      '[useYardAssetVisitFetcher] mode=static ignores initialAssetVisitData/placeholderAssetVisitData. Use staticAssetVisitData.'
    );
  }

  if (isLazyLiveMode(mode) && !isNil(toValue(initialAssetVisitData))) {
    console.warn(
      '[useYardAssetVisitFetcher] mode=lazy-live does not use initialAssetVisitData for the query cache; use placeholderAssetVisitData for partial UI.'
    );
  }

  const lazyLiveUnlocked = ref(!isLazyLiveMode(mode));
  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const wakeLazyLive = () => {
    if (isLazyLiveMode(mode) && !lazyLiveUnlocked.value) {
      lazyLiveUnlocked.value = true;
    }
  };

  const assetVisitQuery = useQuery({
    queryKey: computed(() =>
      effectiveAssetVisitId.value ? queryKeys.assetVisit(effectiveAssetVisitId.value) : []
    ),
    queryFn: async ({ signal }) => {
      if (!effectiveAssetVisitId.value) {
        return null;
      }

      const response = await assetVisitService.getAssetVisitDetails(effectiveAssetVisitId.value, {
        requestConfig: { signal },
        enableCache: true
      });

      return response as YardAssetVisitDetails;
    },
    enabled: computed(() =>
      isEntityPresenterQueryEnabled({
        mode,
        hasEffectiveId: Boolean(effectiveAssetVisitId.value),
        isInViewport: isInViewport.value,
        lazyLiveUnlocked: lazyLiveUnlocked.value
      })
    ),
    initialData: computed(() =>
      isStaticMode(mode) || isLazyLiveMode(mode) ? undefined : initialAssetVisit.value
    ),
    placeholderData: computed(() => {
      if (isStaticMode(mode)) {
        return undefined;
      }
      return placeholderAssetVisit.value as YardAssetVisitDetails | undefined;
    }),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed((): YardAssetVisitDetails | null => {
    const visitData = isStaticMode(mode) ? staticAssetVisit.value : assetVisitQuery.data.value;

    if (!visitData) {
      if (isStaticMode(mode)) {
        console.warn('[useYardAssetVisitFetcher] mode=static requires staticAssetVisitData.');
      }
      return null;
    }

    return visitData;
  });

  const isLoading = computed(() => (isStaticMode(mode) ? false : assetVisitQuery.isLoading.value));
  const isFetching = computed(() =>
    isStaticMode(mode) ? false : assetVisitQuery.isFetching.value
  );
  const error = computed(() => (isStaticMode(mode) ? null : assetVisitQuery.error.value));

  const invalidate = () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    if (effectiveAssetVisitId.value) {
      queryClient.invalidateQueries(
        {
          queryKey: queryKeys.assetVisit(effectiveAssetVisitId.value)
        },
        DEFAULT_INVALIDATE_OPTIONS
      );
    }
  };

  const refresh = async () => {
    if (isStaticMode(mode)) {
      return;
    }
    wakeLazyLive();
    await nextTick();
    await assetVisitQuery.refetch();
  };

  const deferOrInvalidate = () => {
    wakeLazyLive();

    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }

    const isIntersecting = typeof entry === 'boolean' ? entry : (entry?.isIntersecting ?? true);
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleUpdateAssetVisit = (event: DebouncedSocketEvent & { assetVisitId?: string }) => {
    const id = event?.id ?? event?.assetVisitId;
    if (
      matchSocketEventToEntity({ ...event, id }, effectiveAssetVisitId.value) ===
      SocketEventMatch.None
    ) {
      return;
    }

    deferOrInvalidate();
  };

  const handleUpdateAppointment = (event: { id?: string }) => {
    const id = event?.id;
    if (!id || id !== data.value?.appointmentId) {
      return;
    }

    deferOrInvalidate();
  };

  const handleUpdateDock = (event: { id?: string }) => {
    const id = event?.id;
    if (!id || id !== data.value?.appointment?.dockId) {
      return;
    }

    deferOrInvalidate();
  };

  const handleAssetContainerChange = (event: { assetVisitId?: string }) => {
    const id = event?.assetVisitId;
    if (!id || id !== effectiveAssetVisitId.value) {
      return;
    }

    deferOrInvalidate();
  };

  const eventHubHandlers: AssetVisitEventHubHandlerMap = {
    'update-AssetVisit': handleUpdateAssetVisit,
    'create-AssetContainer': handleAssetContainerChange,
    'update-AssetContainer': handleAssetContainerChange,
    'update-Appointment': handleUpdateAppointment,
    'update-Dock': handleUpdateDock
  };

  const subscribeEventHub = () => {
    if (!subscribesToEventHub(mode)) {
      return;
    }

    for (const topic of ASSET_VISIT_EVENT_HUB_TOPICS) {
      eventHub.on(topic, eventHubHandlers[topic]);
    }
  };

  const unsubscribeEventHub = () => {
    if (!subscribesToEventHub(mode)) {
      return;
    }

    for (const topic of ASSET_VISIT_EVENT_HUB_TOPICS) {
      eventHub.off(topic, eventHubHandlers[topic]);
    }
  };

  watch(data, newAssetVisit => {
    if (onUpdateReceived && newAssetVisit) {
      onUpdateReceived(newAssetVisit);
    }
  });

  watch(effectiveAssetVisitId, (newId, oldId) => {
    if (newId !== oldId) {
      pendingInvalidation.value = false;
      lazyLiveUnlocked.value = !isLazyLiveMode(mode);
    }
  });

  onMounted(subscribeEventHub);
  onScopeDispose(unsubscribeEventHub);

  return {
    data,
    isLoading,
    isFetching,
    error,
    refresh,
    invalidate,
    onIntersect: handleIntersect,
    query: assetVisitQuery
  };
}
